import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { designPhases, studioServices } from '../data/portfolioData';
import { Compass, CheckCircle2, ChevronRight, Layers, Box } from 'lucide-react';

export const PhilosophyProcess: React.FC = () => {
  const [activePhaseIndex, setActivePhaseIndex] = useState(0);
  const activePhase = designPhases[activePhaseIndex];

  return (
    <section id="methodology" className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-10 scroll-mt-24">
      {/* Section Header */}
      <div className="space-y-4 max-w-2xl mb-16">
        <div className="flex items-center gap-2 text-xs font-mono text-arch-accent tracking-widest uppercase">
          <Layers className="w-3.5 h-3.5" />
          <span>03 // Architectural Discipline</span>
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white tracking-tight">
          Methodology & <span className="italic font-normal text-arch-limestone">Execution</span>
        </h2>
        <p className="text-xs sm:text-sm text-neutral-400 font-light leading-relaxed">
          From the initial topography and solar angle readings through to millimeter-precision joinery, every commission moves through our systematic 4-phase architectural methodology.
        </p>
      </div>

      {/* 4-Phase Interactive Showcase */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left Phase Selector Column */}
        <div className="lg:col-span-5 space-y-3">
          {designPhases.map((phase, idx) => {
            const isActive = activePhaseIndex === idx;
            return (
              <button
                key={phase.number}
                onClick={() => setActivePhaseIndex(idx)}
                className={`w-full text-left p-5 rounded-sm border transition-all duration-300 flex items-center justify-between ${
                  isActive
                    ? 'bg-white/[0.06] border-arch-accent/60 shadow-lg'
                    : 'bg-white/[0.02] border-white/5 hover:border-white/15 hover:bg-white/[0.04]'
                }`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-mono font-semibold tracking-wider ${
                      isActive ? 'text-arch-accent' : 'text-neutral-500'
                    }`}>
                      PHASE {phase.number}
                    </span>
                    <span className="text-[11px] font-mono text-neutral-400">
                      {phase.subtitle}
                    </span>
                  </div>
                  <div className="text-base sm:text-lg font-serif text-white font-medium">
                    {phase.title}
                  </div>
                </div>

                <ChevronRight className={`w-4 h-4 transition-transform ${
                  isActive ? 'text-arch-accent translate-x-1' : 'text-neutral-600'
                }`} />
              </button>
            );
          })}
        </div>

        {/* Right Active Phase Deep-Dive Card */}
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={activePhase.number}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.4 }}
              className="p-8 sm:p-10 rounded-sm bg-arch-surface/80 border border-white/10 space-y-8 relative overflow-hidden"
            >
              {/* Background Crosshair & Phase Watermark */}
              <div className="absolute top-4 right-6 text-7xl font-serif font-bold text-white/[0.03] select-none pointer-events-none">
                {activePhase.number}
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-mono text-arch-accent uppercase tracking-wider">
                  <Compass className="w-3.5 h-3.5" />
                  <span>Phase Deliverables & Scope</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif text-white font-semibold">
                  {activePhase.title}
                </h3>
                <p className="text-sm sm:text-base text-neutral-300 font-light leading-relaxed">
                  {activePhase.description}
                </p>
              </div>

              {/* Key Deliverables List */}
              <div className="space-y-3 pt-4 border-t border-white/10">
                <div className="text-xs font-mono text-arch-muted uppercase tracking-wider">
                  Client & Regulatory Deliverables
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activePhase.deliverables.map((item) => (
                    <div
                      key={item}
                      className="p-3 rounded bg-black/40 border border-white/5 flex items-center gap-2.5 text-xs text-neutral-300"
                    >
                      <CheckCircle2 className="w-4 h-4 text-arch-accent flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Services Grid */}
      <div className="mt-28 pt-16 border-t border-white/10">
        <div className="space-y-3 mb-12">
          <div className="text-xs font-mono text-arch-accent uppercase tracking-widest flex items-center gap-2">
            <Box className="w-3.5 h-3.5" />
            <span>Scope of Studio Practice</span>
          </div>
          <h2 className="text-3xl font-serif text-white">
            Architectural <span className="italic font-normal text-arch-limestone">Capabilities</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {studioServices.map((service) => (
            <div
              key={service.id}
              className="p-6 rounded-sm bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 hover:border-arch-accent/30 transition-all duration-300 space-y-4"
            >
              <div className="text-xs font-mono text-arch-accent font-semibold">
                SERVICE // {service.number}
              </div>
              <h4 className="text-lg font-serif text-white font-medium">
                {service.name}
              </h4>
              <p className="text-xs text-neutral-400 font-light leading-relaxed">
                {service.description}
              </p>
              <div className="pt-2 border-t border-white/5 space-y-1.5">
                {service.highlights.map((h) => (
                  <div key={h} className="text-[11px] font-mono text-neutral-400 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-arch-accent" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
